import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Database, Search, Sparkles, Phone, MessageSquare, 
  CalendarCheck, DollarSign, Brain, Play, CheckCircle2,
  BarChart3, Users
} from 'lucide-react';
import { Badge } from '../../ui/badge';
import { Dialog, DialogContent, DialogTrigger } from '../../ui/dialog';

const KimCaseAcceptanceSlide = () => {
  const [step, setStep] = useState(0);

  // Animation Sequence
  useEffect(() => {
    const interval = setInterval(() => {
      setStep((prev) => {
        if (prev >= 5) return 0; // Restart loop
        return prev + 1;
      });
    }, 3000); // 3s per step
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="h-full w-full bg-slate-50 flex items-center justify-center p-12 relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none">
             <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-emerald-100/40 rounded-full blur-[80px] -translate-y-1/2 translate-x-1/2" />
             <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-blue-100/40 rounded-full blur-[80px] translate-y-1/2 -translate-x-1/2" />
        </div>

        {/* Floating Agent Header (Top Left) */}
        <div className="absolute top-8 left-8 lg:left-12 z-50 flex items-center gap-4 bg-white/40 backdrop-blur-md p-2 pr-6 rounded-full border border-white/20 shadow-lg">
            <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-white shadow-sm">
                 <img src="/assets/kim.png" alt="Kim" className="w-full h-full object-cover" />
            </div>
            <div className="text-left">
                <h3 className="text-lg font-bold text-slate-900 leading-none">Kim</h3>
                <p className="text-xs text-blue-600 font-bold uppercase tracking-wide">Case Acceptance</p>
            </div>
        </div>

        {/* Demo Button Overlay */}
        <div className="absolute bottom-8 right-8 z-50 flex items-center gap-4">
             {/* Audio Wave Animation */}
             <div className="flex items-center gap-1 h-8">
                {[1, 2, 3, 4, 5].map((i) => (
                    <motion.div
                        key={i}
                        animate={{
                            height: [8, 24, 8],
                            opacity: [0.5, 1, 0.5]
                        }}
                        transition={{
                            duration: 1,
                            repeat: Infinity,
                            delay: i * 0.1,
                            ease: "easeInOut"
                        }}
                        className="w-1 bg-blue-500 rounded-full"
                    />
                ))}
            </div>

            <Dialog>
                <DialogTrigger asChild>
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="group relative inline-flex items-center gap-3 bg-slate-900/90 backdrop-blur-md px-5 py-3 rounded-full shadow-2xl border border-slate-700/50 hover:border-blue-500/50 transition-all duration-300"
                    >
                        <div className="relative flex items-center justify-center w-8 h-8 bg-blue-600 rounded-full text-white shadow-lg group-hover:scale-110 transition-transform duration-300">
                            <Play className="w-3.5 h-3.5 ml-0.5 fill-current" />
                            <div className="absolute inset-0 rounded-full bg-blue-400 animate-ping opacity-20" />
                        </div>
                        <div className="text-left pr-1">
                            <div className="text-xs font-bold text-white">Watch Live Demo</div>
                            <div className="text-[10px] text-slate-300">2 min video</div>
                        </div>
                    </motion.button>
                </DialogTrigger>
                <DialogContent className="max-w-4xl p-0 overflow-hidden bg-black border-none">
                    <div className="relative w-full pt-[62.5%]">
                        <iframe 
                            src="https://www.loom.com/embed/840a2fbe89a24a3d81700f981b4d0b78" 
                            frameBorder="0" 
                            webkitallowfullscreen="true" 
                            mozallowfullscreen="true" 
                            allowFullScreen 
                            className="absolute top-0 left-0 w-full h-full"
                        ></iframe>
                    </div>
                </DialogContent>
            </Dialog>
        </div>

        <div className="flex flex-row h-full w-full max-w-[1600px] gap-8 relative z-10 pt-0">
            {/* Left Side: Context */}
            <div className="w-1/3 flex flex-col justify-center space-y-8">
                
                <div className="space-y-4">
                    <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 leading-tight">
                        Turn unscheduled treatment plans into <span className="text-blue-600">revenue</span>.
                    </h2>
                    
                    <p className="text-lg text-slate-600 leading-relaxed">
                        Kim reads Asha PMS to find patients with pending treatment. She creates intelligent, personalized narratives explaining the value of care, and uses AI voice & SMS to nudge them to book.
                        <br/><br/>
                        <span className="font-semibold text-slate-900">Even a few additional cases per month means thousands in extra revenue.</span>
                    </p>
                </div>

                <div className="flex items-center gap-4 p-4 bg-white rounded-xl border border-slate-200 shadow-sm">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                        <DollarSign className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                        <div className="text-sm text-slate-500 font-medium">Potential Revenue Impact</div>
                        <div className="text-xl font-bold text-slate-900">+$ Thousands / month</div>
                    </div>
                </div>
            </div>

            {/* Right Side: Animated Workflow */}
            <div className="w-2/3 h-full flex items-center justify-center">
                <div className="w-full max-w-2xl relative">
                    {/* Timeline Line */}
                    <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-slate-200 z-0" />

                    <div className="space-y-6 relative z-10">
                        
                        {/* Step 1: Reads PMS */}
                        <WorkflowStep 
                            active={step >= 0}
                            icon={Database}
                            color="blue"
                            title="Step 1: Scans Treatment Plans"
                            desc="Kim reads patient files in Asha PMS to find unscheduled treatment."
                            status="Scanning"
                            statusColor="text-blue-600"
                        />

                        {/* Step 2: Target Patients */}
                        <WorkflowStep 
                            active={step >= 1}
                            icon={Users}
                            color="indigo"
                            title="Step 2: Identifies Opportunities"
                            desc="Filters for high-value cases that haven't been scheduled yet."
                            status="Targeting"
                            statusColor="text-indigo-600"
                        />

                        {/* Step 3: Create Narrative */}
                        <WorkflowStep 
                            active={step >= 2}
                            icon={Brain}
                            color="purple"
                            title="Step 3: Intelligent Narrative"
                            desc="Generates personalized education on why treatment is urgent."
                            status="Generating"
                            statusColor="text-purple-600"
                            highlight={step === 2}
                        />

                        {/* Step 4: Outreach */}
                        <WorkflowStep 
                            active={step >= 3}
                            icon={Phone}
                            color="pink"
                            title="Step 4: Multi-Channel Outreach"
                            desc="Calls and texts the patient to explain value and nudge booking."
                            status="Contacting"
                            statusColor="text-pink-600"
                        />

                        {/* Step 5: Booking */}
                        <WorkflowStep 
                            active={step >= 4}
                            icon={CalendarCheck}
                            color="emerald"
                            title="Step 5: Patient Books"
                            desc="Patient understands the value and schedules their appointment."
                            status="Success"
                            statusColor="text-emerald-600"
                            highlight={step === 4}
                        />

                        {/* Revenue Badge */}
                        <AnimatePresence>
                            {step >= 4 && (
                                <motion.div 
                                    initial={{ opacity: 0, scale: 0.8 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    className="absolute -right-4 top-1/2 -translate-y-1/2 bg-green-600 text-white p-4 rounded-xl shadow-xl z-20"
                                >
                                    <div className="flex items-center gap-2">
                                        <DollarSign className="w-6 h-6" />
                                        <span className="text-2xl font-bold">Revenue</span>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                    </div>
                </div>
            </div>
        </div>
    </div>
  );
};

const WorkflowStep = ({ active, icon: Icon, color, title, desc, status, statusColor, highlight }) => {
    const colorClasses = {
        blue: 'bg-blue-100 text-blue-600 border-blue-200',
        indigo: 'bg-indigo-100 text-indigo-600 border-indigo-200',
        purple: 'bg-purple-100 text-purple-600 border-purple-200',
        pink: 'bg-pink-100 text-pink-600 border-pink-200',
        emerald: 'bg-emerald-100 text-emerald-600 border-emerald-200',
    };

    return (
        <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: active ? 1 : 0.3, x: active ? 0 : 0 }}
            transition={{ duration: 0.5 }}
            className={`relative pl-16 pr-4 ${highlight ? 'scale-105 transition-transform duration-500' : ''}`}
        >
            <div className={`absolute left-4 top-2 w-8 h-8 rounded-full border-4 border-slate-50 flex items-center justify-center z-10 shadow-sm ${active ? colorClasses[color] : 'bg-slate-200 text-slate-400'}`}>
                <Icon className="w-4 h-4" />
            </div>
            
            <div className={`bg-white p-4 rounded-xl border ${highlight ? 'border-blue-300 shadow-md ring-1 ring-blue-100' : 'border-slate-200 shadow-sm'}`}>
                <div className="flex justify-between items-start mb-1">
                    <span className="font-bold text-slate-900 text-sm">{title}</span>
                    <span className={`text-[10px] font-bold uppercase tracking-wider ${statusColor}`}>{status}</span>
                </div>
                <p className="text-xs text-slate-600 leading-relaxed">
                    {desc}
                </p>
            </div>
        </motion.div>
    );
};

export default KimCaseAcceptanceSlide;
